import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import { getRememberedSettingsCustomer } from '@/settingsContext'

function formatUkDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return iso || '—'
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function daysLabel(item) {
  const due = item.field_kind === 'due_date'
  if (item.overdue) {
    const n = Math.abs(item.days_until)
    return n === 1 ? '1 day overdue' : `${n} days overdue`
  }
  if (item.days_until === 0) return due ? 'Due today' : 'Expires today'
  if (item.days_until === 1) return due ? 'Due tomorrow' : 'Expires tomorrow'
  return `In ${item.days_until} days`
}

export function Expiries() {
  const { isAdmin } = useAuth()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [data, setData] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result = await settingsApi.listExpiries()
      setData(result)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not load expiries.',
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAdmin) return undefined
    load()
    return undefined
  }, [isAdmin, load])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  const items = data?.items || []

  return (
    <SettingsShell title="ToDo / Expiring" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl tracking-tight text-brand">
                ToDo / Expiring
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-brand-muted">
                Open tasks from <strong>Bekat IT → ToDo</strong>, plus{' '}
                <strong>Expiry</strong> fields site-wide that are overdue or
                within the warning window
                {data
                  ? ` (${data.warning_days} day${data.warning_days === 1 ? '' : 's'})`
                  : ''}
                . Configure the Expiry window under{' '}
                <Link to="/settings/expiries" className="underline">
                  Settings → Expiry warning
                </Link>
                .
              </p>
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && !data ? (
          <p className="text-sm text-brand-muted">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white/90 px-6 py-8 text-sm text-brand-muted shadow-sm">
            Nothing due soon. Add an <strong>Expiry</strong> or{' '}
            <strong>Due date</strong> column on a table (Edit Fields) to track
            renewals and ToDo items.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white/90 shadow-sm">
            {data?.truncated && (
              <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                Showing the first {items.length} of {data.total_matched} matches.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-brand-muted">
                  <tr>
                    <th className="px-4 py-2.5 font-medium">Customer</th>
                    <th className="px-4 py-2.5 font-medium">Item</th>
                    <th className="px-4 py-2.5 font-medium">Page</th>
                    <th className="px-4 py-2.5 font-medium">Field</th>
                    <th className="px-4 py-2.5 font-medium">Date</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((item) => (
                    <tr
                      key={`${item.row_id}-${item.field_key}`}
                      className={item.overdue ? 'bg-red-50/60' : undefined}
                    >
                      <td className="px-4 py-2.5 font-medium text-brand">
                        {item.customer_name}
                      </td>
                      <td className="px-4 py-2.5 text-brand">{item.item_label}</td>
                      <td className="px-4 py-2.5 text-brand-muted">
                        {item.category_title}
                      </td>
                      <td className="px-4 py-2.5 text-brand-muted">
                        {item.field_label}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-brand">
                        {formatUkDate(item.expiry_date)}
                      </td>
                      <td
                        className={[
                          'px-4 py-2.5 text-xs font-semibold',
                          item.overdue ? 'text-red-800' : 'text-amber-800',
                        ].join(' ')}
                      >
                        {daysLabel(item)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Link
                          to={item.href}
                          className="text-xs font-medium underline"
                        >
                          Open page
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SettingsShell>
  )
}
